import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../layouts/Header";
import { Button, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import axiosInstance from '../../api/axios';
import { toast } from "react-toastify";
import ReusableTable from "../../components/ReusableTable";
import { GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import { useSelector } from "react-redux";

/* ─────────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────────── */
const styles = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&family=Instrument+Serif:ital@0;1&display=swap');

*, *::before, *::after { box-sizing: border-box; }

.om-root {
  font-family: 'DM Sans', sans-serif;
  min-height: 100vh;
  padding: 20px 24px 40px;
}

/* ── PAGE HEADER ── */
.om-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 22px;
  flex-wrap: wrap;
  gap: 12px;
}

.om-title {
  color: #1c1c1c;
  margin: 0;
  font-weight: 600;
  line-height: 1;
}

.om-sub {
  font-size: 12px;
  color: #999;
  margin: 4px 0 0;
  letter-spacing: 0.2px;
}

/* ── FILTER BAR ── */
.om-filters {
  background: #fff;
  border-radius: 14px;
  padding: 14px 18px;
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  box-shadow: 0 1px 8px rgba(0,0,0,0.05);
}

.om-filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 140px;
}

.om-filter-group label {
  font-size: 10.5px;
  font-weight: 600;
  color: #888;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.om-filter-group select,
.om-filter-group input {
  font-size: 12.5px;
  border: 1.5px solid #e8e8e4;
  border-radius: 9px;
  padding: 6px 10px;
  background: #FBFCFE;
  color: #1c1c1c;
  outline: none;
  font-family: 'DM Sans', sans-serif;
  transition: border-color 0.15s;
  height: 34px;
}

.om-filter-group select:focus,
.om-filter-group input:focus { border-color: #1c1c1c; }

.om-search-box {
  display: flex;
  align-items: center;
  border: 1.5px solid #e8e8e4;
  border-radius: 9px;
  background: #FBFCFE;
  overflow: hidden;
  height: 34px;
  flex: 1;
  min-width: 180px;
}

.om-search-box input {
  border: none !important;
  background: transparent !important;
  flex: 1;
  padding: 0 10px;
  font-size: 12.5px;
  outline: none;
  height: 100%;
  font-family: 'DM Sans', sans-serif;
}

.om-search-box button {
  background: #1c1c1c;
  border: none;
  color: #fff;
  padding: 0 12px;
  height: 100%;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.15s;
}

.om-search-box button:hover { background: #333; }

.om-btn {
  height: 34px;
  border: none;
  border-radius: 9px;
  font-size: 12px;
  font-weight: 600;
  padding: 0 16px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'DM Sans', sans-serif;
  transition: opacity 0.15s, transform 0.12s;
  white-space: nowrap;
}

.om-btn:hover { opacity: 0.88; transform: translateY(-1px); }
.om-btn-dark { background: #1c1c1c; color: #fff; }
.om-btn-green { background: #16a34a; color: #fff; }
.om-btn-danger { background: #dc2626; color: #fff; }
.om-btn-outline { background: transparent; border: 1.5px solid #ddd; color: #444; }
.om-btn-blue { background: #2563eb; color: #fff; }

/* ── MAIN WORKSPACE ── */
.om-workspace {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  transition: grid-template-columns 0.35s cubic-bezier(.4,0,.2,1);
}

.om-workspace.panel-open {
  grid-template-columns: 1fr 420px;
}

/* ── OUTLET TABLE CARD ── */
.om-table-card {
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}

.om-table-card .table { font-size: 12.5px; margin: 0; }
.om-table-card .table th {
  background: #141926;
  color: #e2e5ec;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  border-bottom: 1px solid #f0f0ee;
  padding: 11px 14px;
}
.om-table-card .table td {
  padding: 11px 14px;
  vertical-align: middle;
  border-bottom: 1px solid #f8f8f6;
}
.om-table-card .table tbody tr { cursor: pointer; transition: background 0.12s; }
.om-table-card .table tbody tr:hover td { background: #fafbff; }
.om-table-card .table tbody tr.selected td { background: #eff6ff; }
.om-table-card .table tbody tr:last-child td { border-bottom: none; }

/* ── STATUS & BADGES ── */
.om-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  display: inline-block;
}
.om-badge-active   { background: #dcfce7; color: #16a34a; }
.om-badge-inactive { background: #fee2e2; color: #dc2626; }
.om-badge-soon     { background: #fef3c7; color: #d97706; }
.om-badge-primary  { background: #dbeafe; color: #2563eb; }
.om-badge-purple   { background: #ede9fe; color: #7c3aed; }
.om-badge-brown    { background: #fce7d6; color: #9a3412; }

/* ── RIGHT PANEL ── */
.om-panel {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 20px rgba(0,0,0,0.09);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: panelIn 0.3s cubic-bezier(.4,0,.2,1);
  max-height: calc(100vh - 140px);
  position: sticky;
  top: 20px;
}

@keyframes panelIn {
  from { opacity: 0; transform: translateX(20px); }
  to   { opacity: 1; transform: translateX(0); }
}

.om-panel-top {
  padding: 16px 18px 0;
  border-bottom: 1px solid #f0f0ee;
  flex-shrink: 0;
}

.om-panel-outlet-name {
  font-family: 'Instrument Serif', serif;
  font-size: 18px;
  color: #1c1c1c;
  margin: 0 0 2px;
}

.om-panel-outlet-loc {
  font-size: 11px;
  color: #aaa;
  margin: 0 0 12px;
}

/* Tabs */
.om-tabs {
  display: flex;
  gap: 0;
  border-bottom: none;
}

.om-tab {
  font-size: 12px;
  font-weight: 600;
  padding: 8px 16px;
  cursor: pointer;
  border: none;
  background: none;
  color: #aaa;
  border-bottom: 2px solid transparent;
  font-family: 'DM Sans', sans-serif;
  transition: color 0.15s, border-color 0.15s;
}

.om-tab.active {
  color: #1c1c1c;
  border-bottom-color: #1c1c1c;
}

.om-panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 18px;
}

.om-panel-body::-webkit-scrollbar { width: 4px; }
.om-panel-body::-webkit-scrollbar-track { background: transparent; }
.om-panel-body::-webkit-scrollbar-thumb { background: #ddd; border-radius: 4px; }

/* Panel close btn */
.om-panel-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: #f4f3ef;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: background 0.15s;
}
.om-panel-close:hover { background: #e8e8e4; }

/* ── DETAIL ROWS ── */
.om-detail-section {
  margin-bottom: 18px;
}

.om-detail-section-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #bbb;
  margin: 0 0 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f0f0ee;
}

.om-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 5px 0;
  font-size: 12px;
  gap: 8px;
}

.om-detail-row .label { color: #999; flex-shrink: 0; min-width: 110px; }
.om-detail-row .value { color: #1c1c1c; font-weight: 500; text-align: right; word-break: break-word; }

/* Outlet image in panel */
.om-outlet-img {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  object-fit: cover;
  border: 2px solid #f0f0ee;
}

/* ── USERS TAB ── */
.om-users-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.om-users-count {
  font-size: 11px;
  font-weight: 600;
  background: #eff6ff;
  color: #2563eb;
  border-radius: 20px;
  padding: 2px 10px;
}

.om-user-search {
  display: flex;
  align-items: center;
  border: 1.5px solid #e8e8e4;
  border-radius: 8px;
  overflow: hidden;
  background: #fafaf7;
  height: 30px;
  width: 100%;
  margin-bottom: 10px;
}

.om-user-search input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 0 10px;
  font-size: 12px;
  outline: none;
  font-family: 'DM Sans', sans-serif;
}

.om-user-search i { padding: 0 10px; color: #bbb; font-size: 13px; }

.om-user-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.12s;
  margin-bottom: 4px;
}

.om-user-row:hover { background: #fafaf7; }

.om-user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #dbeafe, #ede9fe);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: #2563eb;
  flex-shrink: 0;
  text-transform: uppercase;
}

.om-user-info { flex: 1; min-width: 0; }
.om-user-name { font-size: 12.5px; font-weight: 600; color: #1c1c1c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.om-user-role { font-size: 10.5px; color: #aaa; margin-top: 1px; }

.om-user-actions { display: flex; gap: 4px; }

.om-icon-btn {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  border: none;
  background: #f4f3ef;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: background 0.12s, color 0.12s;
}
.om-icon-btn:hover { background: #e8e8e4; }
.om-icon-btn.danger:hover { background: #fee2e2; color: #dc2626; }
.om-icon-btn.primary:hover { background: #dbeafe; color: #2563eb; }

/* ── SKELETON ── */
.om-skeleton {
  background: linear-gradient(90deg, #ebebeb 25%, #e0e0e0 50%, #ebebeb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border-radius: 6px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── MODALS ── */
.om-modal .modal-content {
  border: none;
  border-radius: 18px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.15);
}

.om-modal .modal-header {
  border-bottom: 1px solid #f0f0ee;
  padding: 16px 22px;
  background: #fafaf8;
  border-radius: 18px 18px 0 0;
}

.om-modal .modal-title {
  font-family: 'Instrument Serif', serif;
  font-size: 18px;
  font-weight: 400;
}

.om-modal .modal-footer {
  border-top: 1px solid #f0f0ee;
  background: #fafaf8;
  border-radius: 0 0 18px 18px;
  padding: 12px 22px;
}

.om-modal .form-label { font-size: 11px; font-weight: 600; color: #777; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; }

.om-modal .form-control,
.om-modal .form-select {
  font-size: 12.5px;
  border-radius: 9px;
  border: 1.5px solid #e8e8e4;
  font-family: 'DM Sans', sans-serif;
  background: #fafaf7;
}

.om-modal .form-control:focus,
.om-modal .form-select:focus {
  border-color: #1c1c1c;
  box-shadow: 0 0 0 3px rgba(28,28,28,0.07);
}

/* Map */
.om-map-wrap {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e8e8e4;
  margin-top: 8px;
}

/* Empty state */
.om-empty {
  text-align: center;
  padding: 32px 16px;
  color: #ccc;
}
.om-empty i { font-size: 36px; display: block; margin-bottom: 8px; opacity: 0.4; }
.om-empty p { font-size: 12px; margin: 0; color: #bbb; }

/* Responsive */
@media (max-width: 900px) {
  .om-workspace.panel-open { grid-template-columns: 1fr; }
  .om-panel { position: relative; top: 0; max-height: none; }
}
`;

/* ─────────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────────── */
const Outlets = () => {
    const navigate = useNavigate();
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const { access } = useSelector((state) => state.login);
    const { loginEmail } = useSelector((state) => state.login);
    const email = loginEmail || localStorage.getItem("email");
    const [outletAccess, setOutletAccess] = useState([]);
    const [userAccess, setUserAccess] = useState([]);
    const accessToken = localStorage.getItem("authToken");

    // ── Outlet state ──
    const [outlets, setOutlets] = useState([]);
    const [filteredOutlets, setFilteredOutlets] = useState([]);
    const [groups, setGroups] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [searchData, setSearchData] = useState({ city: "", status: "", menuGroup: "" });
    const [dataLoading, setDataLoading] = useState(false);
    const [showBtnLoading, setShowBtnLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // ── Panel state ──
    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const [activeTab, setActiveTab] = useState("details"); // "details" | "users"
    const [outletDetails, setOutletDetails] = useState({});
    const [detailLoading, setDetailLoading] = useState(false);

    // ── Users state ──
    const [outletUsers, setOutletUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [userSearch, setUserSearch] = useState("");
    const [usersLoading, setUsersLoading] = useState(false);

    // ── User modals ──
    const [openAddUserModal, setOpenAddUserModal] = useState(false);
    const [openEditUserModal, setOpenEditUserModal] = useState(false);
    const [openDeleteUserModal, setOpenDeleteUserModal] = useState(false);
    const [openViewUserModal, setOpenViewUserModal] = useState(false);
    const [userInputData, setUserInputData] = useState({ outlet: "", name: "", role: "", mobile: "", email: "", password: "" });
    const [editedUserData, setEditedUserData] = useState({});
    const [outletUserDetail, setOutletUserDetail] = useState({});
    const [outletUserId, setOutletUserId] = useState("");
    const [userValidationErrors, setUserValidationErrors] = useState({});
    const [editUserValidationErrors, setEditUserValidationErrors] = useState({});
    const [isAddUserLoading, setIsAddUserLoading] = useState(false);
    const [isEditUserLoading, setIsEditUserLoading] = useState(false);
    const [isDeleteUserLoading, setIsDeleteUserLoading] = useState(false);

    // ── Outlet modals ──
    const [openDeleteOutletModal, setOpenDeleteOutletModal] = useState(false);
    const [openUpdateImageModal, setOpenUpdateImageModal] = useState(false);
    const [outletToDelete, setOutletToDelete] = useState(null);
    const [outletImageId, setOutletImageId] = useState("");
    const [editedOutletImage, setEditedOutletImage] = useState(null);
    const [isDeleteOutletLoading, setIsDeleteOutletLoading] = useState(false);
    const [isUpdateImageLoading, setIsUpdateImageLoading] = useState(false);

    // ── Map ──
    const [center, setCenter] = useState({ lat: 17.9104, lng: 77.5199, name: "" });
    const { isLoaded } = useJsApiLoader({ id: "google-map-script", googleMapsApiKey: "AIzaSyATjJP7Zx7lNzU-dRY9dLNTY29TfUHXPJo" });
    const [map, setMap] = useState(null);
    const onLoad = useCallback((m) => { const b = new window.google.maps.LatLngBounds(center); m.fitBounds(b); setMap(m); }, [center.lat, center.lng]);
    const onUnmount = useCallback(() => setMap(null), []);

    // ── Permissions ──
    useEffect(() => {
        if (Array.isArray(access)) {
            setOutletAccess(access.filter(p => p?.startsWith("Outlet:")));
            setUserAccess(access.filter(p => p?.startsWith("OutletUser:")));
        }
    }, [access]);

    const hasPerm = (prefix, action) => {
        const list = prefix === "Outlet" ? outletAccess : userAccess;
        return list.includes(`${prefix}:${action}`) || email === "contact@xpansecafe.com";
    };

    const formatTime = (time) => {
        if (!time) return "—";
        let [h, m] = time.split(":");
        h = parseInt(h, 10);
        const ampm = h >= 12 ? "PM" : "AM";
        h = h % 12 || 12;
        return `${h}:${m?.padStart(2, "0")} ${ampm}`;
    };

    const getStatusClass = (s) => s === "ACTIVE" ? "active" : s === "COMING_SOON" ? "soon" : "inactive";

    /* ── API: Outlets ── */
    const getOutlets = async (menuG, status, city) => {
        setDataLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            let url = "outlet";
            const params = [];
            if (menuG) params.push(`menuGroup=${menuG}`);
            if (status) params.push(`status=${status}`);
            if (city) params.push(`city=${city}`);
            if (params.length) url += `?${params.join("&")}`;
            const res = await axiosInstance.get(url, { headers });
            if (res?.data?.status) {
                const data = res.data.data.outlets.map((o, i) => ({ ...o, serialNumber: i + 1 }));
                setOutlets(data);
                setFilteredOutlets(data);
            }
        } catch (err) {
            if (err.response?.data?.status === false) toast.error(err.response.data.error);
        } finally {
            setDataLoading(false);
        }
    };

    /* ── API: Download Outlets ── */
    const downloadOutlets = async () => {
    setIsDownloading(true);
    try {
        const headers = { 
            Authorization: `Bearer ${accessToken}`,
        };
        
        // Build URL with filters and isDownload flag
        let url = "outlet";
        const params = [];
        if (searchData.menuGroup) params.push(`menuGroup=${searchData.menuGroup}`);
        if (searchData.status) params.push(`status=${searchData.status}`);
        if (searchData.city) params.push(`city=${searchData.city}`);
        params.push(`isDownload=true`);
        
        if (params.length) url += `?${params.join("&")}`;
        
        const res = await axiosInstance.get(url, { 
            headers,
            responseType: 'blob' // Important for file download
        });
        
        // Check if response is actually a CSV/Excel file or an error
        const contentType = res.headers['content-type'];
        
        // If the response is JSON (error case), parse it as text
        if (contentType && contentType.includes('application/json')) {
            const text = await res.data.text();
            const errorData = JSON.parse(text);
            toast.error(errorData.error || "Failed to download outlets data");
            setIsDownloading(false);
            return;
        }
        
        // Generate standardized filename with timestamp
        const date = new Date();
        const timestamp = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}_${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}${String(date.getSeconds()).padStart(2, '0')}`;
        
        // Create filename with filters info
        let filename = `List of Outlets`;
        if (searchData.status) filename += `_${searchData.status}`;
        if (searchData.city) filename += `of ${searchData.city}`;
        if (searchData.menuGroup) {
            const group = groups.find(g => g._id === searchData.menuGroup);
            if (group) filename += `of ${group.groupName.replace(/\s+/g, '_')}`;
        }
        
        // Determine file extension based on content type
        if (contentType && contentType.includes('text/csv')) {
            filename += '.csv';
        } else if (contentType && contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
            filename += '.xlsx';
        } else if (contentType && contentType.includes('application/vnd.ms-excel')) {
            filename += '.xls';
        } else {
            filename += '.csv'; // default to CSV
        }
        
        // Create download link
        const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        }, 100);
        
        toast.success("Outlets data downloaded successfully!");
    } catch (err) {
        // Handle network errors or other issues
        if (err.response) {
            // If it's a blob response that's actually an error
            if (err.response.data instanceof Blob) {
                try {
                    const text = await err.response.data.text();
                    const errorData = JSON.parse(text);
                    toast.error(errorData.error || errorData.message || "Failed to download outlets data");
                } catch {
                    toast.error("Failed to download outlets data");
                }
            } else {
                toast.error(err.response.data?.error || "Failed to download outlets data");
            }
        } else {
            toast.error("Network error. Please try again.");
        }
    } finally {
        setIsDownloading(false);
    }
};

    const getMenuGroup = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const res = await axiosInstance.get("menu-group", { headers });
            if (res?.data?.status) setGroups(res.data.data.menuGroup);
        } catch (err) { /* silent */ }
    };

    const getSingleOutletDetails = async (id) => {
        setDetailLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const res = await axiosInstance.get(`outlet?_id=${id}`, { headers });
            console.log("Response of Single Outlet Details :", res);
            
            if (res?.data?.status) setOutletDetails(res.data.data.outlets);
        } catch (err) { /* silent */ } finally {
            setDetailLoading(false);
        }
    };

    const deleteOutlet = async () => {
        setIsDeleteOutletLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const res = await axiosInstance.delete("outlet", { data: { _id: outletToDelete }, headers });
            if (res?.data?.status) {
                toast.success(res.data.data.message);
                getOutlets();
                setOpenDeleteOutletModal(false);
                if (selectedOutlet?._id === outletToDelete) setSelectedOutlet(null);
            }
        } catch (err) {
            if (err.response?.data?.status === false) toast.error(err.response.data.error);
        } finally {
            setIsDeleteOutletLoading(false);
        }
    };

    const handleUpdateImage = async (e) => {
        e.preventDefault();
        setIsUpdateImageLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const fd = new FormData();
            fd.append("outletImage", editedOutletImage);
            fd.append("_id", outletImageId);
            const res = await axiosInstance.put("outlet", fd, { headers });
            if (res?.data?.status) {
                toast.success(res.data.data.message);
                getOutlets();
                setOpenUpdateImageModal(false);
                if (selectedOutlet?._id === outletImageId) getSingleOutletDetails(outletImageId);
            }
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || err?.response?.data?.error || "Error updating image");
        } finally {
            setIsUpdateImageLoading(false);
        }
    };

    /* ── API: Users ── */
    const getOutletUsers = async (outId) => {
        if (!outId) return;
        setUsersLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const res = await axiosInstance.get(`outlet/user?outlet=${outId}`, { headers });
            if (res?.data?.status) {
                const users = res.data.data.outletUsers.map((u, i) => ({ ...u, serialNumber: i + 1 }));
                setOutletUsers(users);
                setFilteredUsers(users);
            }
        } catch (err) {
            if (err.response?.data?.status === false) toast.error(err.response.data.error);
        } finally {
            setUsersLoading(false);
        }
    };

    const getSingleUser = async (userId) => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const res = await axiosInstance.get(`outlet/user?_id=${userId}`, { headers });
            console.log("Response of Single outlet User :", res);
            
            if (res?.data?.status) setOutletUserDetail(res.data.data.outletUsers);
        } catch (err) { /* silent */ }
    };

    const validateUserForm = (data, isEdit = false) => {
        const e = {};
        if (!data.outlet) e.outlet = "Required";
        if (!data.name?.trim()) e.name = "Required";
        if (!data.role) e.role = "Required";
        if (!data.mobile) e.mobile = "Required";
        else if (data.mobile.length !== 10) e.mobile = "Must be 10 digits";
        else if (!/^\d+$/.test(data.mobile)) e.mobile = "Digits only";
        if (!data.email?.trim()) e.email = "Required";
        if (!isEdit && !data.password) e.password = "Required";
        if (isEdit && !data.status) e.status = "Required";
        return e;
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        const errors = validateUserForm(userInputData);
        if (Object.keys(errors).length) { setUserValidationErrors(errors); return; }
        setIsAddUserLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const res = await axiosInstance.post("outlet/user", userInputData, { headers });
            if (res?.data?.status) {
                toast.success(res.data.data.message);
                getOutletUsers(selectedOutlet?._id);
                setUserInputData({ outlet: selectedOutlet?._id || "", name: "", role: "", mobile: "", email: "", password: "" });
                setOpenAddUserModal(false);
            }
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || err?.response?.data?.error || "Error");
        } finally {
            setIsAddUserLoading(false);
        }
    };

    const handleEditUser = async (e) => {
        e.preventDefault();
        const errors = validateUserForm(editedUserData, true);
        if (Object.keys(errors).length) { setEditUserValidationErrors(errors); return; }
        setIsEditUserLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const res = await axiosInstance.put("outlet/user", editedUserData, { headers });
            if (res?.data?.status) {
                toast.success(res.data.data.message);
                getOutletUsers(selectedOutlet?._id);
                setOpenEditUserModal(false);
            }
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || err?.response?.data?.error || "Error");
        } finally {
            setIsEditUserLoading(false);
        }
    };

    const handleDeleteUser = async () => {
        setIsDeleteUserLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const res = await axiosInstance.delete("outlet/user", { data: { _id: outletUserId }, headers });
            if (res?.data?.status) {
                toast.success(res.data.data.message);
                getOutletUsers(selectedOutlet?._id);
                setOpenDeleteUserModal(false);
            }
        } catch (err) {
            if (err.response?.data?.status === false) toast.error(err.response.data.error);
        } finally {
            setIsDeleteUserLoading(false);
        }
    };

    /* ── Select outlet → open panel ── */
    const handleSelectOutlet = (outlet) => {
        if (selectedOutlet?._id === outlet._id) {
            setSelectedOutlet(null);
            return;
        }
        setSelectedOutlet(outlet);
        setActiveTab("details");
        getSingleOutletDetails(outlet._id);
        getOutletUsers(outlet._id);
        const lat = parseFloat(outlet?.coordinates?.latitude);
        const lng = parseFloat(outlet?.coordinates?.longitude);
        if (!isNaN(lat) && !isNaN(lng)) setCenter({ lat, lng, name: outlet.name });
    };

    /* ── Tab switch ── */
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === "users" && selectedOutlet) getOutletUsers(selectedOutlet._id);
    };

    /* ── Search / filter ── */
    useEffect(() => {
        if (!searchText.trim()) { setFilteredOutlets(outlets); return; }
        const re = new RegExp(searchText, "i");
        setFilteredOutlets(outlets.filter(o =>
            o.name?.match(re) || o.email?.match(re) || o.phone?.match(re) ||
            o.city?.match(re) || o.state?.match(re) || o.country?.match(re) || o.pincode?.match(re)
        ));
        setPage(1);
    }, [searchText, outlets]);

    useEffect(() => {
        if (!userSearch.trim()) { setFilteredUsers(outletUsers); return; }
        const re = new RegExp(userSearch, "i");
        setFilteredUsers(outletUsers.filter(u =>
            u.name?.match(re) || u.email?.match(re) || u.mobile?.match(re) || u.role?.match(re)
        ));
    }, [userSearch, outletUsers]);

    const getPaginatedData = () => {
        const s = (page - 1) * rowsPerPage;
        return filteredOutlets.slice(s, s + rowsPerPage);
    };

    useEffect(() => { getOutlets(); getMenuGroup(); }, []);

    /* ── Role options ── */
    const roleOptions = [
        { value: "CAFE-MANAGER", label: "Cafe Manager" },
        { value: "MANAGER", label: "Manager" },
        { value: "CASHIER", label: "Cashier" },
    ];
    const statusOptions = [
        { value: "ACTIVE", label: "Active" },
        { value: "INACTIVE", label: "Inactive" },
    ];

    /* ── Table columns ── */
    const columns = [
        { header: "Sr.", accessor: "serialNumber", width: "46px" },
        {
            header: "Outlet",
            accessor: "name",
            render: (value, row) => (
                <div className="d-flex align-items-center gap-2">
                    <img src={row.outletImage} alt="" style={{ width: 34, height: 34, borderRadius: 8, objectFit: "cover", border: "1px solid #f0f0ee" }} />
                    <div>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: "#1c1c1c" }}>{value}</div>
                        <div style={{ fontSize: 10.5, color: "#aaa" }}>{row.city}, {row.state}</div>
                    </div>
                </div>
            ),
        },
        {
            header: "Menu Group", accessor: "menuGroup",
            render: (val) => <span style={{ fontSize: 11.5, color: val ? "#16a34a" : "#dc2626", fontWeight: 500 }}>{val ? val.groupName : "No Menu"}</span>
        },
        {
            header: "Contact", accessor: "phone",
            render: (val) => <span style={{ fontSize: 12, color: "#2563eb" }}>{val}</span>
        },
        {
            header: "Status", accessor: "status",
            render: (val, row) => (
                <div>
                    <span className={`om-badge om-badge-${getStatusClass(val)}`}>{val}</span>
                    <div style={{ fontSize: 10, color: "#aaa", marginTop: 2 }}>{row.orderAcceptType}</div>
                </div>
            )
        },
        {
            header: "Actions", accessor: null, width: "80px",
            render: (_, row) => (
                <div className="d-flex justify-content-center gap-1" onClick={e => e.stopPropagation()}>
                    {hasPerm("Outlet", "update") && (
                        <button className="om-icon-btn primary" title="Edit" onClick={() => navigate(`/outlet-management/edit-outlet/${row._id}`, { state: { outletData: row } })}>
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    {hasPerm("Outlet", "delete") && (
                        <button className="om-icon-btn danger" title="Delete" onClick={() => { setOutletToDelete(row._id); setOpenDeleteOutletModal(true); }}>
                            <i className="ri-delete-bin-line"></i>
                        </button>
                    )}
                    <button className="om-icon-btn" title="View Users" onClick={() => handleSelectOutlet(row)} style={{ color: selectedOutlet?._id === row._id ? "#2563eb" : undefined }}>
                        <i className={selectedOutlet?._id === row._id ? "ri-layout-right-2-fill" : "ri-layout-right-2-line"}></i>
                    </button>
                </div>
            ),
        },
    ];

    /* ─────────────────────────────────────────────
       RENDER
    ───────────────────────────────────────────── */
    return (
        <React.Fragment>
            <style>{styles}</style>
            <Header onSkin={setSkin} />
            <div className="main main-app om-root">

                {/* PAGE HEADER */}
                <div className="om-header">
                    <div>
                        <h3 className="om-title">OUTLET MANAGEMENT</h3>
                        <p className="om-sub">Manage outlets and their staff from one place</p>
                    </div>
                    <div className="d-flex gap-2">
                        {hasPerm("Outlet", "create") && (
                            <button className="om-btn om-btn-dark" onClick={() => navigate("/outlet-management/add-outlet")}>
                                <i className="ri-add-line"></i> Add Outlet
                            </button>
                        )}
                        {hasPerm("Outlet", "get") && (
                            <button 
                                className="om-btn om-btn-blue" 
                                onClick={downloadOutlets}
                                disabled={isDownloading}
                            >
                                {isDownloading ? (
                                    <Spinner size="sm" />
                                ) : (
                                    <i className="ri-download-2-line"></i>
                                )}
                                {isDownloading ? " Downloading..." : " Download"}
                            </button>
                        )}
                    </div>
                </div>

                {/* FILTER BAR */}
                <div className="om-filters">
                    <div className="om-filter-group" style={{ minWidth: 160 }}>
                        <label>Menu Group</label>
                        <select name="menuGroup" value={searchData.menuGroup} onChange={e => setSearchData(p => ({ ...p, menuGroup: e.target.value }))}>
                            <option value="">All groups</option>
                            {groups.map(g => <option key={g._id} value={g._id}>{g.groupName}</option>)}
                        </select>
                    </div>
                    <div className="om-filter-group">
                        <label>Status</label>
                        <select name="status" value={searchData.status} onChange={e => setSearchData(p => ({ ...p, status: e.target.value }))}>
                            <option value="">All</option>
                            <option value="ACTIVE">Active</option>
                            <option value="INACTIVE">Inactive</option>
                            <option value="COMING_SOON">Coming Soon</option>
                        </select>
                    </div>
                    <div className="om-filter-group">
                        <label>City</label>
                        <input type="text" placeholder="Enter city" value={searchData.city} onChange={e => setSearchData(p => ({ ...p, city: e.target.value }))} />
                    </div>
                    <button className="om-btn om-btn-green" onClick={() => getOutlets(searchData.menuGroup, searchData.status, searchData.city)}>
                        {showBtnLoading ? <Spinner size="sm" /> : <><i className="ri-filter-3-line"></i> Apply</>}
                    </button>
                    <div className="om-search-box" style={{ marginLeft: "auto" }}>
                        <i className="ri-search-line" style={{ padding: "0 10px", color: "#bbb", fontSize: 13 }}></i>
                        <input type="text" value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Search outlets..." />
                        <button><i className="ri-search-line"></i></button>
                    </div>
                </div>

                {/* WORKSPACE */}
                <div className={`om-workspace ${selectedOutlet ? "panel-open" : ""}`}>

                    {/* LEFT: TABLE */}
                    <div className="om-table-card">
                        {hasPerm("Outlet", "get") && (
                            <ReusableTable
                                columns={columns}
                                data={getPaginatedData()}
                                rowKey="serialNumber"
                                loading={dataLoading}
                                currentPage={page}
                                recordsPerPage={rowsPerPage}
                                onPageChange={setPage}
                                totalPages={Math.ceil(filteredOutlets.length / rowsPerPage)}
                                onRecordsPerPageChange={(r) => { setPage(1); setRowsPerPage(r); }}
                                rowClassName={(row) => row._id === selectedOutlet?._id ? "selected" : ""}
                                onRowClick={handleSelectOutlet}
                            />
                        )}
                    </div>

                    {/* RIGHT: DETAIL PANEL */}
                    {selectedOutlet && (
                        <div className="om-panel">
                            <button className="om-panel-close" onClick={() => setSelectedOutlet(null)}>
                                <i className="ri-close-line"></i>
                            </button>

                            <div className="om-panel-top">
                                <div className="d-flex align-items-center gap-10 mb-2" style={{ gap: 10 }}>
                                    {outletDetails?.outletImage && (
                                        <img src={outletDetails.outletImage} className="om-outlet-img" alt="outlet" />
                                    )}
                                    <div style={{ flex: 1 }}>
                                        <p className="om-panel-outlet-name">{selectedOutlet.name}</p>
                                        <p className="om-panel-outlet-loc">
                                            <i className="ri-map-pin-line" style={{ marginRight: 3 }}></i>
                                            {[selectedOutlet.city, selectedOutlet.state, selectedOutlet.country].filter(Boolean).join(", ")}
                                        </p>
                                    </div>
                                </div>
                                <div className="om-tabs">
                                    <button className={`om-tab ${activeTab === "details" ? "active" : ""}`} onClick={() => setActiveTab("details")}>
                                        <i className="ri-store-2-line me-1"></i>Details
                                    </button>
                                    <button className={`om-tab ${activeTab === "users" ? "active" : ""}`} onClick={() => handleTabChange("users")}>
                                        <i className="ri-team-line me-1"></i>
                                        Staff
                                        {outletUsers.length > 0 && <span className="om-users-count ms-2">{outletUsers.length}</span>}
                                    </button>
                                    <button className={`om-tab ${activeTab === "map" ? "active" : ""}`} onClick={() => setActiveTab("map")}>
                                        <i className="ri-map-2-line me-1"></i>Map
                                    </button>
                                </div>
                            </div>

                            <div className="om-panel-body">

                                {/* ── DETAILS TAB ── */}
                                {activeTab === "details" && (
                                    detailLoading ? (
                                        <div>
                                            {[1, 2, 3, 4, 5].map(i => (
                                                <div key={i} className="d-flex justify-content-between mb-3">
                                                    <div className="om-skeleton" style={{ width: "35%", height: 12 }}></div>
                                                    <div className="om-skeleton" style={{ width: "45%", height: 12 }}></div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <>
                                            {/* Image change btn */}
                                            {hasPerm("Outlet", "update") && (
                                                <button className="om-btn om-btn-outline w-100 mb-3" style={{ height: 32, fontSize: 11, justifyContent: "center" }}
                                                    onClick={() => { setOutletImageId(outletDetails?._id); setOpenUpdateImageModal(true); }}>
                                                    <i className="ri-image-edit-line"></i> Change Outlet Image
                                                </button>
                                            )}

                                            <div className="om-detail-section">
                                                <p className="om-detail-section-title">Basic Info</p>
                                                {[
                                                    ["Name", outletDetails?.name],
                                                    ["Email", outletDetails?.email],
                                                    ["Phone", outletDetails?.phone],
                                                    ["Address", outletDetails?.address],
                                                    ["City", outletDetails?.city],
                                                    ["Pincode", outletDetails?.pincode],
                                                    ["State", outletDetails?.state],
                                                    ["Country", outletDetails?.country],
                                                    ["GST", outletDetails?.gstin],
                                                    ["FSSAI", outletDetails?.fssai || "Not Provided"],
                                                ].map(([l, v]) => (
                                                    <div key={l} className="om-detail-row">
                                                        <span className="label">{l}</span>
                                                        <span className="value">{v || "—"}</span>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="om-detail-section">
                                                <p className="om-detail-section-title">Operations</p>
                                                {[
                                                    ["Type", outletDetails?.type],
                                                    ["Order Accept", outletDetails?.orderAcceptType],
                                                    ["Opening", formatTime(outletDetails?.openingTime)],
                                                    ["Closing", formatTime(outletDetails?.closingTime)],
                                                    ["Delivery Radius", outletDetails?.minimumDeliveryRadius ? `${outletDetails.minimumDeliveryRadius} km` : "—"],
                                                    ["Menu Group", outletDetails?.menuGroup?.groupName || "All Groups"],
                                                    ["Food Unit", outletDetails?.foodUnitId?._id || "—"],
                                                    ["Beverage Unit", outletDetails?.beverageUnitId?._id || "—"],
                                                    ["Central Unit", outletDetails?.centralUnitId?._id || "—"],
                                                ].map(([l, v]) => (
                                                    <div key={l} className="om-detail-row">
                                                        <span className="label">{l}</span>
                                                        <span className="value">{v || "—"}</span>
                                                    </div>
                                                ))}
                                                <div className="om-detail-row">
                                                    <span className="label">Status</span>
                                                    <span className={`om-badge om-badge-${getStatusClass(outletDetails?.status)}`}>{outletDetails?.status || "—"}</span>
                                                </div>
                                                <div className="om-detail-row">
                                                    <span className="label">Open/Closed</span>
                                                    <span className={`om-badge ${outletDetails?.isOutletClosed ? "om-badge-inactive" : "om-badge-active"}`}>
                                                        {outletDetails?.isOutletClosed ? "CLOSED" : "OPEN"}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="om-detail-section">
                                                <p className="om-detail-section-title">Payment</p>
                                                <div className="om-detail-row">
                                                    <span className="label">Payment Type</span>
                                                    <span className="value">{outletDetails?.paymentType || "—"}</span>
                                                </div>
                                                {outletDetails?.paymentType === "CARD_SWIPE" && (
                                                    <>
                                                        {[
                                                            ["Merchant ID", outletDetails?.pineLabCredentials?.merchantId],
                                                            ["Client ID", outletDetails?.pineLabCredentials?.clientId],
                                                            ["Store ID", outletDetails?.pineLabCredentials?.storeId],
                                                        ].map(([l, v]) => (
                                                            <div key={l} className="om-detail-row">
                                                                <span className="label">{l}</span>
                                                                <span className="value" style={{ fontSize: 11, wordBreak: "break-all" }}>{v || "—"}</span>
                                                            </div>
                                                        ))}
                                                    </>
                                                )}
                                            </div>

                                            {outletDetails?.platForms?.length > 0 && (
                                                <div className="om-detail-section">
                                                    <p className="om-detail-section-title">Delivery Platforms</p>
                                                    {outletDetails.platForms.map((p, i) => (
                                                        <div key={i} className="om-detail-row">
                                                            <span className="label">{p.name}</span>
                                                            <a href={p.webLink} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "#2563eb", textAlign: "right", wordBreak: "break-all" }}>
                                                                {p.webLink}
                                                            </a>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )
                                )}

                                {/* ── USERS TAB ── */}
                                {activeTab === "users" && (
                                    <>
                                        <div className="om-users-header">
                                            <span style={{ fontSize: 12, fontWeight: 600, color: "#666" }}>
                                                Staff Members
                                            </span>
                                            {hasPerm("OutletUser", "create") && (
                                                <button className="om-btn om-btn-dark" style={{ height: 30, fontSize: 11 }}
                                                    onClick={() => {
                                                        setUserInputData({ outlet: selectedOutlet._id, name: "", role: "", mobile: "", email: "", password: "" });
                                                        setUserValidationErrors({});
                                                        setOpenAddUserModal(true);
                                                    }}>
                                                    <i className="ri-add-line"></i> Add
                                                </button>
                                            )}
                                        </div>

                                        <div className="om-user-search">
                                            <i className="ri-search-line"></i>
                                            <input type="text" placeholder="Search staff..." value={userSearch} onChange={e => setUserSearch(e.target.value)} />
                                        </div>

                                        {usersLoading ? (
                                            [1, 2, 3].map(i => (
                                                <div key={i} className="d-flex align-items-center gap-2 mb-3">
                                                    <div className="om-skeleton" style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0 }}></div>
                                                    <div style={{ flex: 1 }}>
                                                        <div className="om-skeleton" style={{ width: "60%", height: 12, marginBottom: 6 }}></div>
                                                        <div className="om-skeleton" style={{ width: "40%", height: 10 }}></div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : filteredUsers.length === 0 ? (
                                            <div className="om-empty">
                                                <i className="ri-user-line"></i>
                                                <p>No staff found</p>
                                            </div>
                                        ) : (
                                            filteredUsers.map(user => (
                                                <div key={user._id} className="om-user-row">
                                                    <div className="om-user-avatar">{user.name?.[0] || "U"}</div>
                                                    <div className="om-user-info">
                                                        <div className="om-user-name">{user.name}</div>
                                                        <div className="om-user-role">{user.role} · {user.mobile}</div>
                                                    </div>
                                                    <span className={`om-badge om-badge-${getStatusClass(user.status)}`} style={{ fontSize: 9 }}>{user.status}</span>
                                                    <div className="om-user-actions">
                                                        {hasPerm("OutletUser", "update") && (
                                                            <button className="om-icon-btn primary" title="Edit" onClick={() => {
                                                                setEditedUserData({ _id: user._id, outlet: user.outlet._id, name: user.name, role: user.role, mobile: user.mobile, email: user.email, status: user.status });
                                                                setOutletUserId(user._id);
                                                                setEditUserValidationErrors({});
                                                                setOpenEditUserModal(true);
                                                            }}>
                                                                <i className="ri-edit-line"></i>
                                                            </button>
                                                        )}
                                                        {hasPerm("OutletUser", "get") && (
                                                            <button className="om-icon-btn" title="View" onClick={() => { getSingleUser(user._id); setOpenViewUserModal(true); }}>
                                                                <i className="ri-eye-line"></i>
                                                            </button>
                                                        )}
                                                        {hasPerm("OutletUser", "delete") && (
                                                            <button className="om-icon-btn danger" title="Delete" onClick={() => { setOutletUserId(user._id); setOpenDeleteUserModal(true); }}>
                                                                <i className="ri-delete-bin-line"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </>
                                )}

                                {/* ── MAP TAB ── */}
                                {activeTab === "map" && (
                                    isLoaded ? (
                                        <div className="om-map-wrap" style={{ height: 320 }}>
                                            <GoogleMap
                                                mapContainerStyle={{ width: "100%", height: "100%" }}
                                                center={center}
                                                zoom={13}
                                                onLoad={onLoad}
                                                onUnmount={onUnmount}
                                            >
                                                <MarkerF position={center} title={center.name} />
                                            </GoogleMap>
                                        </div>
                                    ) : (
                                        <div className="om-empty"><i className="ri-map-2-line"></i><p>Loading map…</p></div>
                                    )
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* ════════════════════════════════════
                    MODALS
                ════════════════════════════════════ */}

                {/* Delete Outlet */}
                <Modal dialogClassName="om-modal" show={openDeleteOutletModal} onHide={() => setOpenDeleteOutletModal(false)} centered size="sm">
                    <Modal.Header closeButton><Modal.Title>Delete Outlet</Modal.Title></Modal.Header>
                    <Modal.Body style={{ padding: "20px 24px", fontSize: 13, textAlign: "center" }}>
                        <i className="ri-error-warning-line" style={{ fontSize: 38, color: "#dc2626", display: "block", marginBottom: 10 }}></i>
                        Are you sure? This action <strong>cannot be undone</strong>.
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="om-btn om-btn-outline" onClick={() => setOpenDeleteOutletModal(false)}>Cancel</button>
                        <button className="om-btn om-btn-danger" onClick={deleteOutlet} disabled={isDeleteOutletLoading}>
                            {isDeleteOutletLoading ? <Spinner size="sm" /> : "Delete"}
                        </button>
                    </Modal.Footer>
                </Modal>

                {/* Update Image */}
                <Modal dialogClassName="om-modal" show={openUpdateImageModal} onHide={() => setOpenUpdateImageModal(false)} centered size="sm">
                    <Modal.Header closeButton><Modal.Title>Update Outlet Image</Modal.Title></Modal.Header>
                    <Form onSubmit={handleUpdateImage}>
                        <Modal.Body style={{ padding: "20px 24px" }}>
                            <Form.Group>
                                <Form.Label>Choose File <span className="text-danger">*</span></Form.Label>
                                <Form.Control type="file" onChange={e => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                        const img = new Image();
                                        img.src = reader.result;
                                        img.onload = () => {
                                            if (img.width === 1410 && img.height === 1100) setEditedOutletImage(file);
                                            else { alert("Image must be 1410×1100 px"); e.target.value = ""; }
                                        };
                                    };
                                    reader.readAsDataURL(file);
                                }} />
                                <Form.Text style={{ fontSize: 11, color: "#888" }}>Dimensions: 1410 × 1100 px</Form.Text>
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <button type="button" className="om-btn om-btn-outline" onClick={() => setOpenUpdateImageModal(false)}>Cancel</button>
                            <button type="submit" className="om-btn om-btn-dark" disabled={isUpdateImageLoading}>
                                {isUpdateImageLoading ? <Spinner size="sm" /> : "Save"}
                            </button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* Add User */}
                <Modal dialogClassName="om-modal" show={openAddUserModal} onHide={() => setOpenAddUserModal(false)} centered size="md">
                    <Modal.Header closeButton>
                        <Modal.Title>Add Staff — {selectedOutlet?.name}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleAddUser}>
                        <Modal.Body style={{ padding: "16px 24px" }}>
                            <Row className="g-3">
                                {[
                                    { label: "Outlet", name: "outlet", type: "select", options: outlets, optLabel: "name", optVal: "_id" },
                                    { label: "Role", name: "role", type: "select", options: roleOptions, optLabel: "label", optVal: "value" },
                                    { label: "Name", name: "name", type: "text" },
                                    { label: "Email", name: "email", type: "email" },
                                    { label: "Mobile", name: "mobile", type: "tel", maxLength: 10 },
                                    { label: "Password", name: "password", type: "password" },
                                ].map(({ label, name, type, options, optLabel, optVal, maxLength }) => (
                                    <Col md={6} key={name}>
                                        <Form.Group>
                                            <Form.Label>{label} <span className="text-danger">*</span></Form.Label>
                                            {type === "select" ? (
                                                <Form.Select size="sm" name={name} value={userInputData[name]}
                                                    onChange={e => { setUserInputData(p => ({ ...p, [name]: e.target.value })); setUserValidationErrors(p => ({ ...p, [name]: "" })); }}>
                                                    <option value="">Select {label}</option>
                                                    {options.map(o => <option key={o[optVal]} value={o[optVal]}>{o[optLabel]}</option>)}
                                                </Form.Select>
                                            ) : (
                                                <Form.Control size="sm" type={type} name={name} value={userInputData[name]} maxLength={maxLength}
                                                    onChange={e => { setUserInputData(p => ({ ...p, [name]: e.target.value })); setUserValidationErrors(p => ({ ...p, [name]: "" })); }} />
                                            )}
                                            {userValidationErrors[name] && <div style={{ fontSize: 10.5, color: "#dc2626", marginTop: 3 }}>{userValidationErrors[name]}</div>}
                                        </Form.Group>
                                    </Col>
                                ))}
                            </Row>
                        </Modal.Body>
                        <Modal.Footer>
                            <button type="button" className="om-btn om-btn-outline" onClick={() => setOpenAddUserModal(false)}>Cancel</button>
                            <button type="submit" className="om-btn om-btn-dark" disabled={isAddUserLoading}>
                                {isAddUserLoading ? <Spinner size="sm" /> : "Add Staff"}
                            </button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* Edit User */}
                <Modal dialogClassName="om-modal" show={openEditUserModal} onHide={() => setOpenEditUserModal(false)} centered size="md">
                    <Modal.Header closeButton><Modal.Title>Edit Staff Member</Modal.Title></Modal.Header>
                    <Form onSubmit={handleEditUser}>
                        <Modal.Body style={{ padding: "16px 24px" }}>
                            <Row className="g-3">
                                {[
                                    { label: "Outlet", name: "outlet", type: "select", options: outlets, optLabel: "name", optVal: "_id" },
                                    { label: "Role", name: "role", type: "select", options: roleOptions, optLabel: "label", optVal: "value" },
                                    { label: "Name", name: "name", type: "text" },
                                    { label: "Email", name: "email", type: "email" },
                                    { label: "Mobile", name: "mobile", type: "tel", maxLength: 10 },
                                    { label: "Status", name: "status", type: "select", options: statusOptions, optLabel: "label", optVal: "value" },
                                ].map(({ label, name, type, options, optLabel, optVal, maxLength }) => (
                                    <Col md={6} key={name}>
                                        <Form.Group>
                                            <Form.Label>{label} <span className="text-danger">*</span></Form.Label>
                                            {type === "select" ? (
                                                <Form.Select size="sm" name={name} value={editedUserData[name]}
                                                    onChange={e => { setEditedUserData(p => ({ ...p, [name]: e.target.value })); setEditUserValidationErrors(p => ({ ...p, [name]: "" })); }}>
                                                    <option value="">Select {label}</option>
                                                    {options.map(o => <option key={o[optVal]} value={o[optVal]}>{o[optLabel]}</option>)}
                                                </Form.Select>
                                            ) : (
                                                <Form.Control size="sm" type={type} name={name} value={editedUserData[name]} maxLength={maxLength}
                                                    onChange={e => { setEditedUserData(p => ({ ...p, [name]: e.target.value })); setEditUserValidationErrors(p => ({ ...p, [name]: "" })); }} />
                                            )}
                                            {editUserValidationErrors[name] && <div style={{ fontSize: 10.5, color: "#dc2626", marginTop: 3 }}>{editUserValidationErrors[name]}</div>}
                                        </Form.Group>
                                    </Col>
                                ))}
                            </Row>
                        </Modal.Body>
                        <Modal.Footer>
                            <button type="button" className="om-btn om-btn-outline" onClick={() => setOpenEditUserModal(false)}>Cancel</button>
                            <button type="submit" className="om-btn om-btn-dark" disabled={isEditUserLoading}>
                                {isEditUserLoading ? "Saving..." : "Save Changes"}
                            </button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* View User */}
                <Modal dialogClassName="om-modal" show={openViewUserModal} onHide={() => setOpenViewUserModal(false)} centered size="sm">
                    <Modal.Header closeButton><Modal.Title>Staff Details</Modal.Title></Modal.Header>
                    <Modal.Body style={{ padding: "16px 24px" }}>
                        <div style={{ background: "#fafaf7", borderRadius: 12, padding: "14px 16px", marginBottom: 12 }}>
                            <p style={{ fontSize: 10, fontWeight: 700, color: "#bbb", letterSpacing: 1, textTransform: "uppercase", margin: "0 0 10px" }}>Personal</p>
                            {[["ID", outletUserDetail?.idNumber], ["Name", outletUserDetail?.name], ["Email", outletUserDetail?.email], ["Role", outletUserDetail?.role], ["Mobile", outletUserDetail?.mobile]].map(([l, v]) => (
                                <div key={l} className="d-flex justify-content-between mb-2" style={{ fontSize: 12 }}>
                                    <span style={{ color: "#aaa" }}>{l}</span>
                                    <span style={{ fontWeight: 500 }}>{v || "—"}</span>
                                </div>
                            ))}
                            <div className="d-flex justify-content-between" style={{ fontSize: 12 }}>
                                <span style={{ color: "#aaa" }}>Status</span>
                                <span className={`om-badge om-badge-${getStatusClass(outletUserDetail?.status)}`}>{outletUserDetail?.status || "—"}</span>
                            </div>
                        </div>
                        <div style={{ background: "#fafaf7", borderRadius: 12, padding: "14px 16px" }}>
                            <p style={{ fontSize: 10, fontWeight: 700, color: "#bbb", letterSpacing: 1, textTransform: "uppercase", margin: "0 0 10px" }}>Outlet</p>
                            {[["Name", outletUserDetail?.outlet?.name], ["Location", [outletUserDetail?.outlet?.city, outletUserDetail?.outlet?.state].filter(Boolean).join(", ")]].map(([l, v]) => (
                                <div key={l} className="d-flex justify-content-between mb-2" style={{ fontSize: 12 }}>
                                    <span style={{ color: "#aaa" }}>{l}</span>
                                    <span style={{ fontWeight: 500 }}>{v || "—"}</span>
                                </div>
                            ))}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="om-btn om-btn-outline" onClick={() => setOpenViewUserModal(false)}>Close</button>
                    </Modal.Footer>
                </Modal>

                {/* Delete User */}
                <Modal dialogClassName="om-modal" show={openDeleteUserModal} onHide={() => setOpenDeleteUserModal(false)} centered size="sm">
                    <Modal.Header closeButton><Modal.Title>Remove Staff</Modal.Title></Modal.Header>
                    <Modal.Body style={{ padding: "20px 24px", fontSize: 13, textAlign: "center" }}>
                        <i className="ri-error-warning-line" style={{ fontSize: 38, color: "#dc2626", display: "block", marginBottom: 10 }}></i>
                        Remove this staff member? This <strong>cannot be undone</strong>.
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="om-btn om-btn-outline" onClick={() => setOpenDeleteUserModal(false)}>Cancel</button>
                        <button className="om-btn om-btn-danger" onClick={handleDeleteUser} disabled={isDeleteUserLoading}>
                            {isDeleteUserLoading ? <Spinner size="sm" /> : "Remove"}
                        </button>
                    </Modal.Footer>
                </Modal>

            </div>
        </React.Fragment>
    );
};

export default Outlets;